import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    inquiryType: '',
    subject: '',
    message: '',
  });

  const [openSnackbar, setOpenSnackbar] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
    setOpenSnackbar(true);
    // Reset form
    setFormData({
      name: '',
      email: '',
      company: '',
      inquiryType: '',
      subject: '',
      message: '',
    });
  };

  return (
    <Box sx={{ py: 8, minHeight: '100vh' }}>
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Typography 
            variant="h2" 
            component="h1" 
            gutterBottom 
            align="center"
            sx={{ fontWeight: 'bold', mb: 2 }}
          >
            Get In Touch
          </Typography>
          <Typography variant="h5" align="center" color="text.secondary" sx={{ mb: 8 }}>
            Let's discuss your game or app project or partnership opportunity
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 4 }}>
            {/* Contact Information */}
            <Box>
              <Stack spacing={3}>
                {[
                  {
                    icon: <EmailIcon fontSize="large" />,
                    title: 'Email Us',
                    content: 'info@innovategames.net',
                    link: 'mailto:info@innovategames.net',
                  },
                  {
                    icon: <PhoneIcon fontSize="large" />,
                    title: 'Call Us',
                    content: '+923710781847',
                    link: 'tel:+923710781847',
                  },
                  {
                    icon: <LocationIcon fontSize="large" />,
                    title: 'Visit Us',
                    content: 'House no. 311 street 17 Villa comunity DHA Bahawalpur',
                    link: null,
                  },
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        backgroundColor: 'background.paper',
                        borderRadius: 3,
                        transition: 'all 0.3s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 16px rgba(99,102,241,0.25)',
                        },
                      }}
                    >
                      <Box sx={{ color: 'primary.main', mb: 2 }}>{item.icon}</Box>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                        {item.title}
                      </Typography>
                      {item.link ? (
                        <Typography
                          component="a"
                          href={item.link}
                          variant="body1"
                          color="text.secondary"
                          sx={{
                            textDecoration: 'none',
                            '&:hover': { color: 'primary.main' },
                          }}
                        >
                          {item.content}
                        </Typography>
                      ) : (
                        <Typography variant="body1" color="text.secondary">
                          {item.content}
                        </Typography>
                      )}
                    </Paper>
                  </motion.div>
                ))}

                {/* Business Hours */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    backgroundColor: 'background.paper',
                    borderRadius: 3,
                  }}
                >
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Business Hours
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Monday - Friday: 9:00 AM - 6:00 PM PKT
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Saturday - Sunday: Closed
                  </Typography>
                </Paper>
              </Stack>
            </Box>

            {/* Contact Form */}
            <Box>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 3, md: 5 },
                    backgroundColor: 'background.paper',
                    borderRadius: 3,
                  }}
                >
                  <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                    Send Us a Message
                  </Typography>
                  <form onSubmit={handleSubmit}>
                    <Stack spacing={3}>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
                        <TextField
                          fullWidth
                          label="Full Name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          variant="outlined"
                        />
                        <TextField
                          fullWidth
                          label="Email Address"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          variant="outlined"
                        />
                      </Box>

                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
                        <TextField
                          fullWidth
                          label="Company (Optional)"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          variant="outlined"
                        />
                        <FormControl fullWidth required>
                            <InputLabel>Inquiry Type</InputLabel>
                            <Select
                              name="inquiryType"
                              value={formData.inquiryType}
                              onChange={handleChange}
                              label="Inquiry Type"
                            >
                              <MenuItem value="game-development">Game Development</MenuItem>
                              <MenuItem value="partnership">Partnership</MenuItem>
                              <MenuItem value="publishing">Publishing</MenuItem>
                              <MenuItem value="careers">Careers</MenuItem>
                              <MenuItem value="press">Press & Media</MenuItem>
                              <MenuItem value="support">Technical Support</MenuItem>
                              <MenuItem value="other">Other</MenuItem>
                            </Select>
                          </FormControl>
                      </Box>

                      <TextField
                        fullWidth
                        label="Subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        variant="outlined"
                      />

                      <TextField
                        fullWidth
                        label="Message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        multiline
                        rows={6}
                        variant="outlined"
                        placeholder="Tell us about your project or inquiry..."
                      />

                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        fullWidth
                        sx={{
                          py: 2,
                          fontSize: '1.1rem',
                          fontWeight: 'bold',
                          background: 'linear-gradient(135deg, #374151 0%, #1F2937 100%)',
                          color: '#FFFFFF',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #1F2937 0%, #374151 100%)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 20px rgba(31,41,55,0.4)',
                          },
                          transition: 'all 0.3s',
                        }}
                      >
                        Send Message
                      </Button>

                      <Typography variant="caption" color="text.secondary" align="center">
                        We typically respond within 24-48 business hours
                      </Typography>
                    </Stack>
                  </form>
                </Paper>
              </motion.div>
            </Box>
          </Box>
        </motion.div>
      </Container>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          Message sent successfully! We'll get back to you soon.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contact; 
